import { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { shallow } from "zustand/shallow";
import { useGetAppRoles } from "@/hooks/useGetAppRoles";
import { useOnboardingStore } from "./useOnboardingStore";
import { assetClassesFromRoles } from "./profileMapping";
import StepProfile from "./steps/StepProfile";
import StepWatchlists from "./steps/StepWatchlists";
import StepDashboard from "./steps/StepDashboard";
import StepConcierge from "./steps/StepConcierge";
import { conciergeEnabled } from "./concierge/api";
import { CTA_PRIMARY, DISPLAY, LINK_MUTED, PILL, ensureDisplayFont } from "./ui/theme";
import type { OnboardingAnswers, OnboardingStep } from "./types";

const STEP_ORDER: OnboardingStep[] = ["profile", "watchlists", "dashboard"];

/**
 * One heading per screen: the modal owns the title so steps render content
 * only — the previous double-heading ("Welcome…" + "Here's what we'll set
 * up") was most of why the panel felt cramped.
 */
const STEP_META: Record<OnboardingStep, { title: string; sub: string }> = {
  profile: {
    title: "What do you trade?",
    sub: "Pick your markets — we shape the desk around them.",
  },
  concierge: {
    title: "Your setup",
    sub: "Built from your own words. Nothing is created until you apply.",
  },
  watchlists: {
    title: "Highlight your names",
    sub: "Your names stand out in the wire. The full feed keeps flowing.",
  },
  dashboard: {
    title: "Your dashboard",
    sub: "A starting layout you can rearrange at any time.",
  },
};

export default function WelcomeModal() {
  const roles = useGetAppRoles();
  const { state, setAnswers, setStep, setSelectedWatchlists, markCompleted, dismiss } =
    useOnboardingStore(
      (s) => ({
        state: s.state,
        setAnswers: s.setAnswers,
        setStep: s.setStep,
        setSelectedWatchlists: s.setSelectedWatchlists,
        markCompleted: s.markCompleted,
        dismiss: s.dismiss,
      }),
      shallow
    );

  useEffect(ensureDisplayFont, []);

  const step: OnboardingStep = state?.lastStep ?? "profile";
  // "concierge" replaces the watchlists/dashboard steps visually (slot 1).
  const stepIndex = step === "concierge" ? 1 : STEP_ORDER.indexOf(step);
  const meta = STEP_META[step];

  // Local working copies — persisted on step transitions, not every keystroke.
  const [answers, setLocalAnswers] = useState<OnboardingAnswers>(
    () => state?.answers ?? { assetClasses: assetClassesFromRoles(roles) }
  );
  const [selectedIds, setSelectedIds] = useState<number[]>(
    () => state?.selectedWatchlistIds ?? []
  );

  const goToStep = (next: OnboardingStep) => {
    setAnswers(answers);
    if (step === "watchlists") setSelectedWatchlists(selectedIds);
    setStep(next);
  };

  // Concierge branch point: free text present + backend enabled → the LLM
  // builds the setup instead of the manual watchlist/template steps.
  const handleContinueFromProfile = () => {
    if (conciergeEnabled && answers.freeText?.trim()) goToStep("concierge");
    else goToStep("watchlists");
  };

  const handleComplete = (appliedTemplateId: string) => {
    setAnswers(answers);
    setSelectedWatchlists(selectedIds);
    markCompleted(appliedTemplateId);
  };

  // Steps with their own CTAs own the whole footer.
  const ownsFooter = step === "dashboard" || step === "concierge";

  return (
    <Dialog open onClose={() => {}} className="relative z-[100] no-drag">
      <DialogBackdrop className="fixed inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="fixed inset-0 z-10 flex items-center justify-center sm:p-6">
        <DialogPanel className="relative flex h-full w-full flex-col overflow-hidden bg-[#0a0a0c] shadow-[0_32px_120px_rgba(0,0,0,0.62)] sm:h-auto sm:max-h-[88vh] sm:max-w-4xl sm:rounded-2xl sm:border sm:border-white/[0.08]">
          {/* Atmosphere: one ambient bloom + hairline grid, both inert. */}
          <div
            aria-hidden
            className="nq-onb-bloom pointer-events-none absolute -right-24 -top-32 h-[420px] w-[520px] rounded-full bg-squawk-blue/[0.07] blur-[150px]"
          />
          <div aria-hidden className="nq-onb-grid pointer-events-none absolute inset-0" />

          <div className="relative flex flex-1 flex-col overflow-y-auto px-6 py-7 sm:px-9 sm:py-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-6">
              <span className={PILL}>
                Setup
                <span className="text-squawk-blue/50">/</span>
                {stepIndex + 1} of {STEP_ORDER.length}
              </span>
              <button type="button" onClick={dismiss} className={LINK_MUTED}>
                Skip for now
              </button>
            </div>

            {/* Segmented progress rail */}
            <div className="mt-5 flex items-center gap-1.5" aria-hidden>
              {STEP_ORDER.map((s, i) => (
                <span
                  key={s}
                  className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${
                    i < stepIndex
                      ? "bg-squawk-blue/40"
                      : i === stepIndex
                        ? "bg-squawk-blue shadow-[0_0_12px_-2px_rgba(0,174,239,0.8)]"
                        : "bg-white/[0.07]"
                  }`}
                />
              ))}
            </div>

            {/* Title — the only heading on screen */}
            <div key={step} className="nq-onb-rise mt-7">
              <DialogTitle
                className={`${DISPLAY} text-[26px] text-white sm:text-[32px]`}
              >
                {meta.title}
              </DialogTitle>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-400">
                {meta.sub}
              </p>
            </div>

            {/* Step body */}
            <div className="mt-7 flex-1">
              {step === "profile" && (
                <StepProfile answers={answers} onChange={setLocalAnswers} />
              )}
              {step === "concierge" && (
                <StepConcierge
                  answers={answers}
                  onComplete={handleComplete}
                  onFallback={() => goToStep("watchlists")}
                />
              )}
              {step === "watchlists" && (
                <StepWatchlists
                  answers={answers}
                  selectedIds={selectedIds}
                  onChange={setSelectedIds}
                />
              )}
              {step === "dashboard" && (
                <StepDashboard
                  answers={answers}
                  selectedWatchlistIds={selectedIds}
                  onComplete={handleComplete}
                />
              )}
            </div>

            {/* Footer */}
            <div className="mt-8 flex items-center justify-between gap-4 border-t border-white/[0.06] pt-5">
              <button
                type="button"
                onClick={() =>
                  goToStep(
                    step === "concierge"
                      ? "profile"
                      : STEP_ORDER[Math.max(0, stepIndex - 1)]
                  )
                }
                className={`${LINK_MUTED} ${stepIndex === 0 ? "invisible" : ""}`}
              >
                ← Back
              </button>
              {!ownsFooter && (
                <button
                  type="button"
                  onClick={() =>
                    step === "profile"
                      ? handleContinueFromProfile()
                      : goToStep(STEP_ORDER[stepIndex + 1])
                  }
                  disabled={step === "profile" && answers.assetClasses.length === 0}
                  className={CTA_PRIMARY}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
